import { Box, Button, Card, Chip, LinearProgress, Link, Tooltip, Typography } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";

import { CheckCircleOutline, CheckCircleRounded, Delete, ThumbDownOutlined, ThumbUpOutlined } from "@mui/icons-material";
import { isEmpty } from "lodash";
import MaterialReactTable from "material-react-table";
import ReactMarkdown from "react-markdown";
import useFetch from "../../hooks/http/useFetch";
import { usePageContext } from "../../hooks/process/usePageContext";
import { axiosInstance } from "../../services/axios";
import { IAlertState, useAlertState } from "../../store/alertState";
import ActionButton, { RISK_STATE_OPTIONS } from "../analysis/ViewFindings/shared/button";
import { ColumnHeaderFilter } from "../analysis/ViewIssues/IssuesTable";
import CommonPopper from "../common/commonPopper";
import TruncatedScrollingText from "../common/truncatedScrollingText";
import ReactMarkdownStyled from "../common/reactMarkdownStyled";

const dummyEmbeddingsData = {
  embeddings: [
    {
      id: "1",
      index: 101,
      risk: "High",
      state: "Open",
      assigneeId: "user_1",
      jiraUrl: "https://jira.company.com/browse/ABC-123",
      jiraNumber: "ABC-123",
      projectName: "Payment Gateway",
      assetName: "payment-service",
      operationId: "op-345",
      findingName: "SQL Injection vulnerability",
      duration: "5 days",
      embeddingsAvailable: "Yes",
      allowedForEmbedding: true,
    },
    {
      id: "2",
      index: 102,
      risk: "Medium",
      state: "In Progress",
      assigneeId: "user_2",
      jiraUrl: "https://jira.company.com/browse/XYZ-789",
      jiraNumber: "XYZ-789",
      projectName: "User Authentication",
      assetName: "auth-service",
      operationId: "op-678",
      findingName: "Weak password encryption",
      duration: "2 days",
      embeddingsAvailable: "Yes",
      allowedForEmbedding: false,
    },
    {
      id: "3",
      index: 103,
      risk: "Low",
      state: "Resolved",
      assigneeId: "user_3",
      jiraUrl: "https://jira.company.com/browse/LMN-456",
      jiraNumber: "LMN-456",
      projectName: "Analytics Service",
      assetName: "analytics-engine",
      operationId: "op-901",
      findingName: "Missing HTTP headers",
      duration: "12 days",
      embeddingsAvailable: "No",
      allowedForEmbedding: true,
    },
  ]
};


const format = require("string-template")

const RecommendationsTable: FC<any> = ({
  history,
  match,


}) => {
  const [columnHeaderFilter, setColumnHeaderFilter] = useState<any>({});
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState(false)
  const { selectedGroup, selectedProject } = usePageContext();

  const { setMessage, clearMessage } = useAlertState(
    (state) => state
  ) as IAlertState;
  const {
    data: embeddingsData,
    loading,
    fetchData,

  } = useFetch(`/ai/triage/issues/recommendations`);



  const updateEmbedding = async (id: string, patchData: any) => {
    try {
      await axiosInstance.patch(`/ai/triage/issues/${id}`, {
        ...patchData
      });
      setMessage({
        title: "Updated successfully",
        type: "success",
      });
    } catch (error) {
      setMessage({
        title: "Error updating",

        type: "error",
      });
    }
  };

  const deleteEmbedding = async (id: string) => {
    try {
      await axiosInstance.delete(`/issues/${id}/recommendation`);
      setMessage({
        title: "Deleted successfully",
        type: "success",
      });

    } catch (error) {
      setMessage({
        title: "Error deleting",

        type: "error",
      });
    }
  };
  const generateEmbeddings = async () => {
    setGeneratingEmbeddings(true)
    try {
      await axiosInstance.post(`/ai/triage/embeddings`);
      window.location.reload()
    } catch (error) {

    }
    setGeneratingEmbeddings(false)
  };

  const embeddings = useMemo(
    () => embeddingsData?.issues || [],
    [embeddingsData, dummyEmbeddingsData]
  );

  const handleColumnHeaderFilterChange = (key: string, value: any[] | null) => {
    if (!isEmpty(value)) {
      setColumnHeaderFilter({ ...columnHeaderFilter, [key]: value! });
    } else {
      let newFilter = { ...columnHeaderFilter };
      delete newFilter[key];
      setColumnHeaderFilter({ ...newFilter });
    }
  };
  return (
    <Box>
      {generatingEmbeddings && <LinearProgress />}
      <Button sx={{ mb: 2, display: 'flex' }} size="small" onClick={generateEmbeddings} variant="outlined" disabled={generatingEmbeddings}>
        Generate Embeddings
      </Button>

      <MaterialReactTable muiTablePaperProps={{
        elevation: 0
      }} enableGlobalFilter={false}
        columns={[

          {
            accessorKey: "id",
            header: "ID",
            Cell: (params: any) => (
              <Link
                onClick={() => {
                  history.push(
                    `/findings?issueId=${params.row?.original?.id}`
                  );
                }}
              >
                #{params.row?.original?.id?.split("-")?.[0]}
              </Link>
            ),
            size: 30,
          },
          {
            accessorKey: "risk",
            header: "Risk",
            Cell: (params: any) => (
              <ActionButton
                issueId={params?.row?.original?.id}
                buttons="RISK"
                risk={params?.row?.original?.risk}
                state={params?.row?.original?.state}
                user={params?.row?.original?.assigneeId}
                jira={{
                  url: params?.row?.original?.jiraUrl,
                  number: params?.row?.original?.jiraNumber,
                }}
              />
            ),
            Header: (
              <ColumnHeaderFilter
                columnKey="risk"
                name="Risk"
                options={RISK_STATE_OPTIONS}
                setColumnHeaderFilter={handleColumnHeaderFilterChange}
              />
            ),
            size: 30,
          },
          {
            accessorKey: "faultId",
            header: "Issue Info",
            size: 100,
            Cell: (params: any) => <Box>
              <CommonPopper
                elevatePopper
                trigger={<Chip className="hand" label={params?.row?.original?.faultId} size="small"></Chip>}
                child={<Box sx={{ p: 2, maxWidth: "50vw", wordWrap: "break-word" }}>
                  <Typography variant="overline">Reason</Typography>
                  <ReactMarkdownStyled
                    markdown={params?.row?.original?.reason}
                  />
                  <Typography variant="overline">Remedy</Typography>
                  <ReactMarkdownStyled
                    markdown={params?.row?.original?.remedy}
                  />
                </Box>}
              />

            </Box>
          }, {
            accessorKey: "isImportant",
            header: "User Feedback",
            size: 100,
            Cell: (params: any) => <Box>

              <CommonPopper
                elevatePopper
                trigger={params?.row?.original?.isImportant ? <ThumbUpOutlined color="success" className="hand" /> : <ThumbDownOutlined color="error" className="hand" />}
                child={<Box sx={{ p: 2 }}>
                  <Typography variant="overline">Comment</Typography>
                  <br />
                  {params?.row?.original?.comment || <i>No Comment By User</i>}
                </Box>}
              />


            </Box>
          },
          {
            accessorKey: "allowedForEmbedding",

            header: "Allowed for embeddings",
            Cell: (params: any) => (
              <Box>
                <AllowedForEmbeddings params={params} updateEmbedding={updateEmbedding} deleteEmbedding={deleteEmbedding} />
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display:"block" }}>
                  {"hasEmbeddings" in params?.row?.original ? params?.row?.original?.hasEmbeddings ? "Embeddings Generated" : "No Embeddings Available" : "No Embeddings Available"}
                </Typography>
              </Box>
            ),
          },


          {
            accessorKey: "name",
            header: "Name",
            size: 100,
            Cell: (params: any) => (
              <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "start" }}>
                  <TruncatedScrollingText
                    name={params?.row?.original?.projectName}
                    typographyVariant='caption'


                  /> &nbsp; /&nbsp;
                  <TruncatedScrollingText
                    name={params?.row?.original?.assetName}
                    typographyVariant='caption'


                  />
                </Box>

                <Typography>{params?.row?.original?.name}</Typography>
              </Box>
            )


          }
        ]}
        data={embeddings}
        initialState={{
          density: "compact",

        }}
        enableColumnActions={false}
        enableSorting={false}
        enableStickyHeader
        enableTopToolbar={false}
        enableBottomToolbar={false}


        enablePagination={false}
        enableDensityToggle={false}
        enableColumnFilters={false}
        // enableRowVirtualization
        state={{
          showProgressBars: loading,
        }}
      />
    </Box>
  );
};


const AllowedForEmbeddings: FC<any> = ({ params, updateEmbedding, deleteEmbedding }) => {
  const [deleted, setDeleted] = useState(false)
  const [checked, setChecked] = useState(params?.row?.original?.allowedForEmbedding)
  if(deleted){
    return <Chip size="small" color="error" label="Removed"/>
  }
  return <Box sx={{ display: "flex", justifyContent: "start" }}>
    {checked ?
      <Button size="small" color="success" variant="contained" startIcon={<CheckCircleRounded
      />} onClick={() => {
        setChecked(false);
        updateEmbedding(params?.row?.original?.id, { allowedForEmbedding: false })
      }}>Allowed</Button> : <Button size="small" color="success" variant="outlined" startIcon={<CheckCircleOutline
      />} onClick={() => {
        setChecked(true);
        updateEmbedding(params?.row?.original?.id, { allowedForEmbedding: true })
      }}>Not Allowed</Button>}
    <Tooltip title="This action will remove embeddings and user feedback.">
      <Button size="small" color="error" startIcon={<Delete />} onClick={() => { setDeleted(true); deleteEmbedding(params?.row?.original?.id) }}>Remove</Button>
    </Tooltip>

  </Box>
}

export default withRouter(RecommendationsTable);
